import React, { useEffect, useRef, useState } from "react";
import { getPageItems, getTotalPages } from "../../utils";

/**
 * Shared list pager. Number buttons use `getPageItems` (1 … current … last).
 *
 * showPagination:
 *   omit / not false → hide until `total > pageSize` (Subscriptions / Campaign Detail)
 *   false → show whenever `total > 0` (WhatsApp contacts)
 *
 * loading:
 *   pass boolean on server-paged lists so active page / Showing stay on the
 *   loaded page until the fetch finishes (click would otherwise jump first).
 */
export default function ListPaginationBar({
  page,
  setPage,
  total,
  pageSize,
  totalPages,
  ariaLabel,
  showPagination = true,
  className,
  style,
  loading,
}) {
  const size = pageSize || 1;
  const pages = totalPages != null ? totalPages : getTotalPages(total, size);
  const hidden = showPagination === false //? total === 0 : total <= size;
  const requested = Math.min(Math.max(1, Number(page) || 1), Math.max(1, pages));
  const [shownPage, setShownPage] = useState(requested);
  const [pending, setPending] = useState(false);
  const sawLoadingRef = useRef(false);

  useEffect(() => {
    if (hidden) {
      setPending(false);
      sawLoadingRef.current = false;
      return;
    }
    if (loading === true) {
      sawLoadingRef.current = true;
      return;
    }
    if (pending && loading === false && !sawLoadingRef.current) {
      return;
    }
    setPending(false);
    sawLoadingRef.current = false;
    setShownPage(requested);
  }, [hidden, requested, loading, pending, total, pages]);

  if (hidden) return null;

  const busy = pending || loading === true;
  const safePage = Math.min(Math.max(1, shownPage), Math.max(1, pages));
  const from = (safePage - 1) * size + 1;
  const to = Math.min(safePage * size, total);
  const go = (n) => {
    const next = Math.min(Math.max(1, n), Math.max(1, pages));
    if (next === safePage || busy) return;
    if (loading === true || loading === false) setPending(true);
    setPage(next);
  };
  return (
    <div
      className={`d-flex align-items-center justify-content-between pagination_align ${className || "px-2 py-2"}`}
      style={style}
    >
      <div className="d-flex align-items-center gap-2">
        <span className="text-muted">Showing</span>
        <span className="pagination_showing">
          {from} to {to} of {total} results
        </span>
      </div>
      <nav aria-label={ariaLabel}>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${safePage <= 1 || busy ? "disabled" : ""}`}>
            <button type="button" className="page-link" onClick={() => go(1)} disabled={safePage <= 1 || busy}>«</button>
          </li>
          <li className={`page-item ${safePage <= 1 || busy ? "disabled" : ""}`}>
            <button type="button" className="page-link" onClick={() => go(Math.max(1, safePage - 1))} disabled={safePage <= 1 || busy}>‹</button>
          </li>
          {getPageItems(pages, safePage).map((p, idx) =>
            p === "…" ? (
              <li className="page-item disabled" key={`e-${idx}`}>
                <span className="page-link">…</span>
              </li>
            ) : (
              <li className={`page-item ${safePage === p ? "active" : ""}`} key={`p-${p}`}>
                <button type="button" className="page-link" onClick={() => go(p)} disabled={busy}>{p}</button>
              </li>
            )
          )}
          <li className={`page-item ${safePage >= pages || busy ? "disabled" : ""}`}>
            <button type="button" className="page-link" onClick={() => go(Math.min(pages, safePage + 1))} disabled={safePage >= pages || busy}>›</button>
          </li>
          <li className={`page-item ${safePage >= pages || busy ? "disabled" : ""}`}>
            <button type="button" className="page-link" onClick={() => go(pages)} disabled={safePage >= pages || busy}>»</button>
          </li>
        </ul>
      </nav>
    </div>
  );
}
